import moment from "moment";
import { useEffect, useState, useMemo } from "react";
import { Check, Close, FilterList } from "@mui/icons-material";
import { Card, Grid, IconButton, Tooltip } from "@mui/material";

import Box from "../../components/Box";
import Helpers from "../../commons/helpers";
import Button from "../../components/Button";
import useStrings from "../../hooks/useStrings";
import ControlChip from "../../components/Chip";
import Resources from "../../constants/resources";
import Typography from "../../components/Typography";
import DatePicker from "../../components/DatePicker";
import Autocomplete from "../../components/Autocomplete";
import StatusCell from "../../components/DataTable/StatusCell";
import DataTableFilter from "../../components/DataTable/Filter";
import CustomPagination from "../../components/CustomPagination";
import useContainerInvitation from "../../hooks/useContainerInvitation";
import InvitationService from "../../services/identity/invitation.service";

import { MainComponent } from "../main";
import { Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { ICodename, IInvitation } from "../../commons/interfaces";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



export interface IRequestGetPagedMyInvitation {
    tenantCode?: string;
    serviceCode?: string;
    organizationId?: string;

    status?: Status;
    validTo?: string;
    validFrom?: string;

    orderBy?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
}

interface IModel {
    requestData?: IRequestGetPagedMyInvitation;
    requestDataTemp?: IRequestGetPagedMyInvitation;
}

interface IProps {
    request: IRequestGetPagedMyInvitation;
    onGetPaged: (query: string, totalCount: number) => void;
}

const MyInvitationContainer = ({ request, onGetPaged }: IProps) => {
    const Strings = useStrings();

    const {
        handlAcceptInvitation,
        handlDeclineInvitation,
    } = useContainerInvitation();

    const {
        userInfo,
        language,
        tenantCode,
        serviceCode,
        onError,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [model, setModel] = useState<IModel>({} as IModel);
    const [dataRows, setDataRows] = useState<IInvitation[]>([]);
    const [openModalFilter, setOpenModalFilter] = useState<boolean>(false);

    const listStatusInvitation: ICodename[] = useMemo(
        () => [
            { code: Status.Draft, name: Strings.Common.DRAFT },
            { code: Status.Accept, name: Strings.Common.ACCEPTED },
            { code: Status.Active, name: Strings.Common.INVITING },
            { code: Status.Deleted, name: Strings.Common.DELETED },
            { code: Status.Decline, name: Strings.Common.REJECTED },
            { code: Status.Inactive, name: Strings.Common.EXPIRED },
        ],
        [Strings]
    );

    const statusCell = (status: number, validTo: number) => {
        let data: any;
        switch (status) {
            case Status.Active:
                if (validTo < moment().unix()) {
                    data = { icon: "report", color: "secondary", title: Strings.Common.EXPIRED };
                } else {
                    data = { icon: "history", color: "warning", title: Strings.Common.INVITING };
                }
                break;
            case Status.Accept:
                data = { icon: "done", color: "success", title: Strings.Common.ACCEPTED };
                break;
            case Status.Draft:
                data = { icon: "drafts", color: "secondary", title: Strings.Common.DRAFT };
                break;
            case Status.Deleted:
                data = { icon: "remove", color: "error", title: Strings.Common.DELETED };
                break;
            case Status.Decline:
                data = { icon: "block", color: "error", title: Strings.Common.REJECTED };
                break;
            default:
                data = { icon: "sync", color: "warning", title: Strings.Common.INVITING };
                break;
        };
        return <StatusCell data={data} />;
    };

    useEffect(() => {
        (
            async () => {
                onShowLoading();

                const newDataReq: IRequestGetPagedMyInvitation = {
                    totalCount: request.totalCount,
                    pageSize: request.pageSize || model?.requestData?.pageSize,
                    pageNumber: request.pageNumber || model?.requestData?.pageNumber,

                    status: request.status || model?.requestData?.status,
                    orderBy: request.orderBy || model?.requestData?.orderBy,
                    validTo: request.validTo || model?.requestData?.validTo,
                    validFrom: request.validFrom || model?.requestData?.validFrom,
                };

                await getPagedInvitation(newDataReq);

                onHideLoading();
            }
        )();
    }, [request, userInfo?.userProfile?.organizationId]);

    const getPagedInvitation = async (newDataReq?: IRequestGetPagedMyInvitation) => {
        try {
            onShowLoading();

            const pageSize = newDataReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newDataReq?.pageNumber || 1, pageSize, newDataReq?.totalCount || 0);

            const reqTemp: any = {
                isIndividual: true,
                status: newDataReq?.status,
                validTo: newDataReq?.validTo,
                validFrom: newDataReq?.validFrom,

                pageSize: pageSize,
                pageNumber: pageNumber || 1,

                tenantCode,
                serviceCode,
                organizationId: userInfo?.userProfile?.organizationId,
            };
            const resultGetPaged = await InvitationService.getPaged(reqTemp);

            setModel({
                requestData: {
                    ...newDataReq,
                    pageSize: resultGetPaged?.pageSize,
                    totalCount: resultGetPaged?.totalCount,
                    pageNumber: resultGetPaged?.currentPage,
                },
                requestDataTemp: {
                    ...newDataReq,
                    pageSize: resultGetPaged?.pageSize,
                    totalCount: resultGetPaged?.totalCount,
                    pageNumber: resultGetPaged?.currentPage,
                },
            });

            let query: string = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

            if (!Helpers.isNullOrEmpty(newDataReq?.status)) {
                query += `&status=${newDataReq?.status}`;
            }
            if (!Helpers.isNullOrEmpty(newDataReq?.validTo)) {
                query += `&validTo=${newDataReq?.validTo}`;
            }
            if (!Helpers.isNullOrEmpty(newDataReq?.validFrom)) {
                query += `&validFrom=${newDataReq?.validFrom}`;
            }

            setDataRows(resultGetPaged.items);
            onGetPaged(query, resultGetPaged.totalCount);
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const renderValueDate = (value: any) => {
        if (Helpers.isNullOrEmpty(value) || value === "0" || value === 0) {
            return undefined;
        } else {
            return (value * 1000);
        };
    }

    const getContentInvitation = (item: IInvitation) => {
        let text: any;

        if (Helpers.isNullOrEmpty(item.resourceName)) {
            if (Helpers.isNullOrEmpty(item.organizationName)) {
                text = Strings.format(
                    Strings.INVITATION.CONTENT_INVITING_3,
                    item.organizationName?.value?.[language],
                );
            } else {
                text = Strings.format(
                    Strings.INVITATION.CONTENT_INVITING_2,
                    item.serviceName,
                );
            }

            if (Helpers.isNullOrEmpty(item.roleNames)) {
                text = text + Strings.format(
                    Strings.INVITATION.WITH_ROLE,
                    item.roleNames,
                );
            }
        } else {
            text = Strings.format(
                Strings.INVITATION.CONTENT_INVITING_1,
                item.resourceName?.value?.[language],
                item.serviceName,
            );
        }

        return text;
    }

    const renderModalFilter = () => (
        <DataTableFilter variant="permanent" ownerState={{ openConfigurator: openModalFilter }}>
            <Box p={2} alignItems="center">
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{Strings.Common.FILTER}</Typography>
                    <IconButton style={{ padding: 0 }} onClick={() => {
                        setOpenModalFilter(false);
                        const newReq = { ...model.requestData };
                        setModel({ ...model, requestDataTemp: newReq });
                    }}>
                        <Close />
                    </IconButton>
                </Box>
                <Box mt={1}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                data={listStatusInvitation || []}
                                label={Strings.Common.STATUS}
                                placeholder={Strings.Common.STATUS}
                                defaultValue={model.requestDataTemp?.status}
                                onChange={(value) => {
                                    setModel(prev => {
                                        let temp: any = {
                                            ...prev,
                                            requestDataTemp: {
                                                ...prev.requestDataTemp,
                                                status: value,
                                            },
                                        };
                                        return temp;
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DatePicker
                                label={Strings.INVITATION.VALID_FROM}
                                placeholder={Strings.INVITATION.VALID_FROM}
                                value={renderValueDate(model.requestDataTemp?.validFrom)}
                                onChangeValue={(value: any) => {
                                    const newVal = value ? moment(value).unix() : undefined;
                                    setModel(prev => {
                                        let temp: any = {
                                            ...prev,
                                            requestDataTemp: {
                                                ...prev.requestDataTemp,
                                                validFrom: newVal,
                                            },
                                        };
                                        return temp;
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DatePicker
                                label={Strings.INVITATION.VALID_TO}
                                placeholder={Strings.INVITATION.VALID_TO}
                                value={renderValueDate(model.requestDataTemp?.validTo)}
                                onChangeValue={(value: any) => {
                                    const newVal = value ? moment(value).unix() : undefined;
                                    setModel(prev => {
                                        let temp: any = {
                                            ...prev,
                                            requestDataTemp: {
                                                ...prev.requestDataTemp,
                                                validTo: newVal,
                                            },
                                        };
                                        return temp;
                                    });
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box
                    mt={2}
                    gap={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ position: "absolute", bottom: 10, right: 0, left: 0 }}
                >
                    <Button color="secondary" onClick={() => {
                        getPagedInvitation({ pageNumber: 1, pageSize: Constants.ROW_PER_PAGE });
                    }}>
                        {Strings.Common.RESET}
                    </Button>
                    <Button onClick={() => {
                        getPagedInvitation({ ...model.requestDataTemp, pageNumber: 1 })
                    }}>
                        {Strings.Common.APPLY}
                    </Button>
                </Box>
            </Box>
        </DataTableFilter>
    );

    const renderCardItemInvitation = (item: IInvitation) => {
        const isDisable = [Status.Deleted, Status.Accept, Status.Decline].includes(item.status);

        return (
            <Card sx={{
                height: "100%",
                padding: "1rem",
                position: "relative",
            }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <img
                        alt="testimonial-item-preview"
                        src={item?.organizationLogoUrl || Resources.IMAGES.THUMBNAIL}
                        style={{
                            width: "90%",
                            border: "none",
                            height: "225px",
                            overflow: "hidden",
                            objectFit: "cover",
                            marginTop: "-2rem",
                            borderRadius: "1rem",
                            background: "#d3d3d3",
                            position: "relative",
                            boxShadow: "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px",
                        }}
                    />
                </Box>
                <Box sx={{
                    margin: "1rem",
                    height: "150px",
                    display: "flex",
                    flexFlow: "column",
                    justifyContent: "end",
                }}>
                    <Box>
                        {statusCell(item.status, Number(item.validTo))}
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <Typography
                            variant="button"
                            sx={{
                                overflow: "hidden",
                                webkitLineClamp: 3,
                                display: "-webkit-box",
                                textOverflow: "ellipsis",
                                webkitBoxOrient: "vertical",
                            }}
                        >
                            {getContentInvitation(item)}
                        </Typography>
                    </Box>

                    {
                        <Box
                            display="flex"
                            marginTop="1rem"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Box
                                color="#ccc"
                                display="flex"
                                borderRadius="8px"
                                alignItems="center"
                                border=" 1px solid #ccc"
                            >
                                {
                                    <Tooltip title={Strings.Common.REJECT}>
                                        <IconButton
                                            color="secondary"
                                            onClick={() => {
                                                handlDeclineInvitation({
                                                    id: item.id,
                                                    onCallBack() {
                                                        getPagedInvitation(model.requestData);
                                                    },
                                                });
                                            }}
                                            disabled={(Number(item.validTo) < moment().unix()) || isDisable}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Tooltip>
                                }
                                |
                                {
                                    <Tooltip title={Strings.Common.REJECT}>
                                        <IconButton
                                            color="info"
                                            onClick={() => {
                                                handlAcceptInvitation({
                                                    id: item.id,
                                                    onCallBack() {
                                                        getPagedInvitation(model.requestData);
                                                    },
                                                });
                                            }}
                                            disabled={(Number(item.validTo) < moment().unix()) || isDisable}
                                        >
                                            <Check />
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Box>
                        </Box>
                    }
                </Box>
            </Card >
        )
    }

    return (
        <MainComponent>
            <>
                {renderModalFilter()}
                <Box mb={1} display="flex" alignItems="flex-end" justifyContent="end">
                    <Box sx={{
                        marginLeft: "8px",
                        marginRight: "16px",
                        borderRadius: "50px",
                        border: "1px solid #ccc",
                        backgroundColor: "#e5e7eb",
                    }}>
                        <IconButton onClick={() => {
                            setOpenModalFilter(true);
                        }}>
                            <FilterList />
                        </IconButton>
                    </Box>
                </Box>
                {(!Helpers.isNullOrEmpty(model.requestData?.validFrom) ||
                    !Helpers.isNullOrEmpty(model.requestData?.validTo) ||
                    !Helpers.isNullOrEmpty(model.requestData?.status)) && (
                        <Box mb={1}>
                            {!Helpers.isNullOrEmpty(model.requestData?.status) && (
                                <ControlChip
                                    label={Strings.Common.STATUS}
                                    value={(listStatusInvitation || []).find((el) => el.code === model.requestData?.status)?.name}
                                    onDelete={() => {
                                        getPagedInvitation({ ...model.requestData, status: undefined });
                                    }}
                                />
                            )}
                            {
                                !Helpers.isNullOrEmpty(model.requestData?.validFrom) &&
                                <ControlChip
                                    label={Strings.INVITATION.VALID_FROM}
                                    value={moment(renderValueDate(model.requestData?.validFrom)).format("DD-MM-YYYY")}
                                    onDelete={() => {
                                        getPagedInvitation({ ...model.requestData, validFrom: undefined });
                                    }}
                                />
                            }
                            {
                                !Helpers.isNullOrEmpty(model.requestData?.validTo) &&
                                <ControlChip
                                    label={Strings.INVITATION.VALID_TO}
                                    value={moment(renderValueDate(model.requestData?.validTo)).format("DD-MM-YYYY")}
                                    onDelete={() => {
                                        getPagedInvitation({ ...model.requestData, validTo: undefined });
                                    }}
                                />
                            }
                        </Box>
                    )}

                <Box p={3}>
                    <Box mb={1}>

                    </Box>
                    <Grid container spacing={3}>
                        {(dataRows?.length > 0) &&
                            dataRows.map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                    {renderCardItemInvitation(item)}
                                </Grid>
                            ))
                        }
                        {(dataRows?.length <= 0) &&
                            <Grid item xs={12} textAlign="center">
                                <Typography variant="button">{Strings.Common.NO_DATA}</Typography>
                            </Grid>
                        }
                    </Grid>
                </Box>

                <Box mt={2}>
                    <CustomPagination
                        page={model?.requestData?.pageNumber}
                        total={model?.requestData?.totalCount}
                        pageSize={model?.requestData?.pageSize}
                        rowsPerPageOptions={Constants.ROW_PER_PAGE_OPTIONS}
                        onChangePage={(value) => {
                            getPagedInvitation({ ...model.requestData, pageNumber: value });
                        }}
                        onChangePageSize={(value) => {
                            getPagedInvitation({ ...model.requestData, pageSize: value });
                        }}
                    />
                </Box>
            </>
        </MainComponent>
    );
};

export default MyInvitationContainer;
